import React, { useState, useEffect, useRef } from "react"
import { ComposableMap, Geographies, Geography } from "react-simple-maps"
import { geoMiller } from "d3-geo-projection"
import LendingMapPatterns from "./lending-map-patterns"
import findObject from "../helpers/find-object"
import { roundNumber } from "../helpers/numbers"
import IconExternalLink from "../components/svg/icon-external-link"
import FocusLock from "react-focus-lock"

// Data imports
import geography from "../data/world-110m-custom-shapes.json"
import lakes from "../data/world-110m-lakes.json"
import lendingMapData from "../data/lending-map-data.json"
import lendingMapAgreements from "../data/lending-map-agreements.json"
import lendingMapFilters from "../data/lending-map-filters.json"

// MAP NOTES:
// - File: '../data/world-110m-custom-shapes.json'
// - Source: https://international.ipums.org/international/gis.shtml
// - Converted to TopoJSON with https://mapshaper.org/
//     Simplification settings: 0.40%, use planar geometry, Visvalingam/weighted area method
// - Merged with data of '../data/raw/world-110m-raw.json' from React Simple Maps
// - Data modified to match IMF data/taxonomies

// Create the lending map
function LendingMap() {
  const lendingMapComponent = useRef()
  const mapWrapper = useRef()
  const map = useRef()
  const mapOverlayRef = useRef()
  const [mapIsActive, setMapIsActive] = useState(false)
  const [mapFilter, setMapFilter] = useState("all") // Accepts a filter slug
  const [mapTooltipActive, setMapTooltipActive] = useState(false)
  const [mapTooltipCountry, setMapTooltipCountry] = useState("") // Accepts a country ID
  const [mapTooltipCoordinates, setMapTooltipCoordinates] = useState({
    x: -999,
    y: -999,
  }) // Position off screen as a safeguard
  const [mapAgreementHighlights, setMapAgreementHighlights] = useState("") // Accepts a country ID
  const [mapAgreementButtonHovered, setMapAgreementButtonHovered] = useState("")
  const [mapOverlayIsActive, setMapOverlayIsActive] = useState(false)
  const [
    mapOverlayAgreementAbbreviation,
    setMapOverlayAgreementAbbreviation,
  ] = useState("")

  // Setup the projection
  const width = 800
  const height = 450

  const customProjection = geoMiller()
    .translate([width / 2, height / 2])
    .rotate([-10, 0, 0])

  // Process and store the lending map data *once* into datasets based on the lending map filters
  var processedData = []

  lendingMapFilters.forEach(filter => {
    if (filter.slug) {
      if (filter.slug === "all") {
        processedData[filter.slug] = lendingMapData
      } else {
        processedData[filter.slug] = filterDataByFilter(
          lendingMapData,
          filter.slug
        )
      }
    }
  })

  // Update the current dataset according to the mapFilter state
  var filteredData = processedData[mapFilter]

  //
  // Event Handlers
  //

  function toggleMapOverlay(agreementAbbreviation) {
    const BODY_LOCK = "util__body-lock"

    if (mapOverlayIsActive) {
      setMapOverlayIsActive(false)
      document.body.classList.remove(BODY_LOCK)
    } else {
      setMapOverlayIsActive(true)
      setMapOverlayAgreementAbbreviation(agreementAbbreviation)
      document.body.classList.add(BODY_LOCK)
    }
  }

  function handleScroll() {
    if (
      lendingMapComponent.current.getBoundingClientRect().top >=
        window.innerHeight * 0.45 ||
      lendingMapComponent.current.getBoundingClientRect().bottom <=
        window.innerHeight * 0.85
    ) {
      setMapIsActive(false)
    } else {
      setMapIsActive(true)
    }
  }

  function handleCountryMouseEnter(e) {
    setMapTooltipActive(true)
    setMapTooltipCountry(e.target.id)
    setMapAgreementHighlights(e.target.id)

    let targetRect = e.target.getBoundingClientRect()
    let rect = lendingMapComponent.current.getBoundingClientRect()

    // Set coordinates as relative to the center-right position of the target country shape
    setMapTooltipCoordinates({
      x: targetRect.right - rect.left,
      y: targetRect.top + targetRect.height / 2 - rect.top,
    })
  }

  function handleCountryMouseLeave() {
    setMapTooltipActive(false)
    setMapAgreementHighlights("")
  }

  function handleSelectChange(e) {
    setMapFilter(e.target.value)
  }

  // Simple listener to close overlay on escape key hit
  function handleKeydown(e) {
    if (mapOverlayIsActive && e.keyCode === 27) {
      toggleMapOverlay()
    }
  }

  //
  // Effects
  //

  useEffect(() => {
    handleScroll() // Initial call makes sure loads/reloads render the map correctly whatever the scroll position

    window.addEventListener("scroll", handleScroll)
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  })

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown)
    return () => {
      document.removeEventListener("keydown", handleKeydown)
    }
  })

  return (
    <section
      id="lending-map"
      tabIndex="-1"
      className={
        "lending-map" +
        (mapIsActive ? " lending-map--is-active" : "") +
        (mapOverlayIsActive ? " lending-map--overlay-is-active" : "")
      }
      ref={lendingMapComponent}
    >
      <header className="lending-map__header">
        <div>
          <h2 className="title--secondary title--uppercase">
            Lending and Debt Relief
          </h2>
          <p className="title--meta title--light title--uppercase">
            (In millions of Special Drawing Rights)
          </p>
          <p className="lending-map__header__meta">
            Lending map as of September 15, 2020. Visit{" "}
            <a href="https://www.imf.org">imf.org</a> for the latest
            information.
          </p>
        </div>
        <div className="lending-map__filters-wrapper">
          <header
            id="lending-map__filters__header"
            className="title--meta title--light title--uppercase"
          >
            Filter By
          </header>
          <div
            className="lending-map__filters"
            aria-labelledby="lending-map__filters__header"
          >
            {lendingMapFilters.map(filter => {
              return (
                <button
                  key={`filter-button-${filter.slug}`}
                  className="lending-map__filters__button"
                  aria-pressed={filter.slug === mapFilter}
                  aria-controls="lending-map"
                  onClick={() => {
                    mapIsActive && setMapFilter(filter.slug)
                  }}
                >
                  <div className="title--meta title--bold title--uppercase">
                    <div
                      className="lending-map__filters__button__indicator"
                      role="presentation"
                    ></div>
                    {filter.title}
                  </div>
                  <p className="lending-map__filters__button__description">
                    {filter.description}
                  </p>
                </button>
              )
            })}
          </div>
          <select
            name="filters"
            id="filters-select"
            className="lending-map__filters__select"
            value={mapFilter}
            onChange={e => {
              mapIsActive && handleSelectChange(e)
            }}
            aria-labelledby="lending-map__filters__header"
            aria-label="Filter the lending map results"
            aria-controls="lending-map"
          >
            {lendingMapFilters.map(filter => {
              return (
                <option
                  key={`filter-select-${filter.slug}`}
                  value={filter.slug}
                >
                  {filter.title}
                </option>
              )
            })}
          </select>
        </div>
      </header>

      <LendingMapPatterns />

      <div className="lending-map__map-wrapper" ref={mapWrapper}>
        <div
          className="lending-map__map-ref lending-map__map-ref--fixed"
          ref={map}
        >
          <ComposableMap
            className="lending-map__map"
            projection={customProjection}
            height={350}
          >
            <Geographies
              className="lending-map__map__geography"
              geography={geography}
            >
              {({ geographies }) => {
                return geographies
                  .filter(geo => geo.properties.CONTINENT !== "Antarctica")
                  .map((geo, index) => {
                    const { ISO_A2 } = geo.properties

                    let country = findObject(lendingMapData, "iso_a2", ISO_A2)
                    let filteredCountry = findObject(
                      filteredData,
                      "iso_a2",
                      ISO_A2
                    )
                    let activeCountry =
                      (mapIsActive &&
                        filteredCountry &&
                        !mapAgreementButtonHovered) ||
                      (mapIsActive &&
                        filteredCountry &&
                        mapAgreementButtonHovered &&
                        findObject(
                          filteredCountry.agreements,
                          "abbreviation",
                          mapAgreementButtonHovered
                        ))

                    // Return a grey base shape for all countries
                    // Plus, return an additional filled shape for all countries that match the unfiltered data
                    // States and filtered data control which filled shapes are visible or not, via className. See lending-map.scss
                    return (
                      <g key={geo.rsmKey}>
                        <Geography
                          key={`base-${geo.rsmKey}-${index}`}
                          id={`base-${ISO_A2}-${index}`}
                          geography={geo}
                          fill="#7A7875"
                          tabIndex="-1"
                        />
                        {country && (
                          <Geography
                            key={`fill-${geo.rsmKey}-${index}`}
                            id={ISO_A2}
                            className={
                              activeCountry
                                ? "fill fill--active"
                                : "fill fill--inactive"
                            }
                            geography={geo}
                            fill={getFill(country)}
                            tabIndex="-1"
                            onMouseEnter={handleCountryMouseEnter}
                            onMouseLeave={handleCountryMouseLeave}
                            aria-haspopup={activeCountry ? "true" : null}
                            aria-controls={
                              activeCountry ? "lending-map__tooptip" : null
                            }
                          />
                        )}
                      </g>
                    )
                  })
              }}
            </Geographies>

            <Geographies className="lending-map__map__lakes" geography={lakes}>
              {({ geographies }) => {
                return geographies.map((geo, index) => {
                  return (
                    <Geography
                      key={`lake-${geo.rsmKey}-${index}`}
                      geography={geo}
                      tabIndex="-1"
                    />
                  )
                })
              }}
            </Geographies>
          </ComposableMap>
        </div>
      </div>

      <LendingMapTooltip
        data={filteredData}
        mapTooltipActive={mapTooltipActive}
        mapTooltipCountry={mapTooltipCountry}
        mapTooltipCoordinates={mapTooltipCoordinates}
      />

      <LendingMapFooter
        data={filteredData}
        mapIsActive={mapIsActive}
        mapAgreementHighlights={mapAgreementHighlights}
        toggleMapOverlay={toggleMapOverlay}
        mapAgreementButtonHovered={mapAgreementButtonHovered}
        setMapAgreementButtonHovered={setMapAgreementButtonHovered}
      />

      <LendingMapOverlay
        mapOverlayRef={mapOverlayRef}
        data={filteredData}
        mapFilter={mapFilter}
        toggleMapOverlay={toggleMapOverlay}
        mapOverlayIsActive={mapOverlayIsActive}
        mapOverlayAgreementAbbreviation={mapOverlayAgreementAbbreviation}
      />
    </section>
  )
}

//
// Subcomponents
//

// Creates the lending map tooltip, containing the information of the country passed to it
function LendingMapTooltip({
  data,
  mapTooltipCountry,
  mapTooltipCoordinates,
  mapTooltipActive,
}) {
  let country = findObject(data, "iso_a2", mapTooltipCountry)

  return (
    <div
      id="lending-map__tooptip"
      className="lending-map__tooptip"
      data-active={mapTooltipActive}
      style={{
        top: mapTooltipCoordinates.y + "px",
        left: mapTooltipCoordinates.x + "px",
      }}
    >
      {country && (
        <div>
          <h4 className="title--quaternary title--uppercase">{country.name}</h4>
          <ul>
            {country.agreements.map((countryAgreement, index) => {
              let agreement = findObject(
                lendingMapAgreements,
                "abbreviation",
                countryAgreement.abbreviation
              )

              return (
                <li
                  key={`tooltip-agreement-${country.iso_a2}-${countryAgreement.abbreviation}-${countryAgreement.amount}-${index}`}
                >
                  {countryAgreement.amount === 0 ? (
                    <h3 className="title--micro title--bold">
                      * (See Agreement Overlay)
                    </h3>
                  ) : (
                    <h3 className="title--meta">
                      {roundNumber(countryAgreement.amount, "millions", "all")}
                      <span className="title--micro title--bold">M SDR</span>
                    </h3>
                  )}
                  <AgreementTypeKey
                    type={countryAgreement.type}
                    color={agreement.color.hex}
                    augmented={
                      countryAgreement.augmented
                        ? countryAgreement.augmented
                        : null
                    }
                  />
                  <p className="lending-map__tooptip__approval">
                    Approved on {countryAgreement.approval_date}
                  </p>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

// Creates the lending map footer, containing the agreements list with numbers for the data passed to it
function LendingMapFooter({
  data,
  mapIsActive,
  mapAgreementHighlights,
  toggleMapOverlay,
  mapAgreementButtonHovered,
  setMapAgreementButtonHovered,
}) {
  let highlightedCountry = findObject(data, "iso_a2", mapAgreementHighlights)

  return (
    <footer className="lending-map__footer">
      <h2 className="title--micro title--bold title--uppercase">
        Click on a Lending Agreement to See More Info
      </h2>
      <div className="lending-map__footer__agreements-list">
        {lendingMapAgreements.map((agreement, index) => {
          let agreementTotal = roundNumber(
            getAgreementTotal(data, agreement.abbreviation),
            "millions"
          )

          let agreementTotalIsNotZero = parseFloat(agreementTotal) > 0

          return (
            <button
              key={`lending-map-agreement-button-${agreement.abbreviation}`}
              className={
                (!highlightedCountry &&
                  ((!mapAgreementButtonHovered && agreementTotalIsNotZero) ||
                    mapAgreementButtonHovered === agreement.abbreviation)) ||
                (highlightedCountry &&
                  findObject(
                    highlightedCountry.agreements,
                    "abbreviation",
                    agreement.abbreviation
                  ))
                  ? "lending-map__footer__agreements-list__button"
                  : "lending-map__footer__agreements-list__button lending-map__footer__agreements-list__button--inactive"
              }
              type="button"
              aria-haspopup="true"
              aria-label={`Open details overlay for ${agreement.type}`}
              aria-controls="lending-map__overlay"
              tabIndex={agreementTotalIsNotZero ? "0" : "-1"}
              onClick={() => {
                mapIsActive &&
                  agreementTotalIsNotZero &&
                  toggleMapOverlay(agreement.abbreviation)
              }}
              onMouseEnter={() => {
                agreementTotalIsNotZero &&
                  setMapAgreementButtonHovered(agreement.abbreviation)
              }}
              onMouseLeave={() => {
                agreementTotalIsNotZero && setMapAgreementButtonHovered("")
              }}
            >
              <h3 className="title--tertiary">
                {agreementTotal}
                <span className="title--micro title--light title--uppercase">
                  M&nbsp;SDR
                </span>
              </h3>
              <AgreementTypeKey
                type={agreement.type}
                color={agreement.color.hex}
                uppercase="true"
              />
            </button>
          )
        })}
      </div>
      <div className="lending-map__footer__meta">
        <div className="lending-map__footer__meta__exchange-rates">
          <h3>SDR/USD Exchange Rates</h3>
          <p className="title--meta title--light title--uppercase">
            April 30, 2020: SDR1 = US$1.36640
          </p>
          <p className="title--meta title--light title--uppercase">
            September 15, 2020: SDR1 = US$1.41664
          </p>
        </div>
        <h4 className="title--micro title--light">
          Country borders do not necessarily reflect the IMF's official
          position.
          <br />
          Aug. = Augmentation
        </h4>
      </div>
    </footer>
  )
}

// Creates the lending map overlay, containing the information of the agreement passed to it
function LendingMapOverlay({
  mapOverlayRef,
  data,
  mapFilter,
  toggleMapOverlay,
  mapOverlayIsActive,
  mapOverlayAgreementAbbreviation,
}) {
  let agreement = findObject(
    lendingMapAgreements,
    "abbreviation",
    mapOverlayAgreementAbbreviation
  )

  let filterTitle = findObject(lendingMapFilters, "slug", mapFilter).title

  return (
    <FocusLock
      disabled={mapOverlayIsActive ? false : true}
      returnFocus={true}
      noFocusGuards={true}
    >
      <section
        id="lending-map__overlay"
        className="lending-map__overlay"
        aria-hidden={mapOverlayIsActive ? "false" : "true"}
        ref={mapOverlayRef}
        tabIndex="-1"
      >
        {agreement && (
          <div>
            <header className="lending-map__overlay__header">
              <h3
                className="title--secondary title--uppercase"
                style={{ color: agreement.color.hex }}
              >
                {agreement.type}
              </h3>
              <button
                className="lending-map__overlay__close-button"
                type="button"
                aria-label="Close map overlay"
                aria-controls="site-mobile-nav"
                onClick={mapOverlayIsActive && toggleMapOverlay}
              ></button>
            </header>
            <div className="lending-map__overlay__agreement-total">
              <h3 className="title--tertiary">
                {roundNumber(
                  getAgreementTotal(data, agreement.abbreviation),
                  "millions"
                )}
                <span className="title--micro title--uppercase">
                  {" "}
                  Milllion SDR{mapFilter !== "all" ? ` in  ${filterTitle}` : ""}
                </span>
              </h3>
            </div>
            <div className="lending-map__overlay__content">
              <div className="lending-map__overlay__content-column">
                {agreement.details.length === 1 ? (
                  <AgreementOverlaySection
                    key={`agreement-overlay-${agreement.abbreviation}-${agreement.details[0].title}`}
                    title={agreement.details[0].title}
                    content={agreement.details[0].content}
                  />
                ) : (
                  <div className="lending-map__overlay__content-column__inner-columns">
                    {agreement.details.map(detail => (
                      <AgreementOverlaySection
                        key={`agreement-overlay-${agreement.abbreviation}-${detail.title}`}
                        title={detail.title}
                        content={detail.content}
                      />
                    ))}
                  </div>
                )}
                {agreement.footnotes && (
                  <div
                    className="lending-map__overlay__content__footnotes util__desktop-only"
                    dangerouslySetInnerHTML={{ __html: agreement.footnotes }}
                  />
                )}
              </div>
              <div className="lending-map__overlay__content-column">
                <AgreementOverlaySectionTable
                  title="Distribution of Funds"
                  data={data}
                  agreementAbbreviation={agreement.abbreviation}
                />
                {agreement.footnotes && (
                  <div
                    className="lending-map__overlay__content__footnotes util__mobile-only"
                    dangerouslySetInnerHTML={{ __html: agreement.footnotes }}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </section>
    </FocusLock>
  )
}

// Overlay subcomponent
function AgreementOverlaySection({ title, content }) {
  return (
    <div className="lending-map__overlay__content__section">
      <h4 className="title--quaternary title--bold">{title}</h4>
      <div dangerouslySetInnerHTML={{ __html: content }} />
    </div>
  )
}

// Overlay subcomponent
function AgreementOverlaySectionTable({ title, data, agreementAbbreviation }) {
  let countries = filterDataByAgreement(data, agreementAbbreviation)

  return (
    <div className="lending-map__overlay__content__section">
      <h4 className="title--quaternary title--bold">{title}</h4>
      <table>
        <thead>
          <tr>
            <th className="title--meta title--bold">Country</th>
            <th className="title--meta title--bold">Date of Approval</th>
            <th className="title--meta title--bold">Amount (SDR)</th>
            <th className="title--meta title--bold">Press Release</th>
          </tr>
        </thead>
        <tbody>
          {countries &&
            countries.map(country => {
              return (
                country.agreements &&
                country.agreements.map((agreement, index) => {
                  return (
                    <tr
                      key={`lending-map-overlay-table-${agreementAbbreviation}-${country.iso_a2}-${index}`}
                      className={
                        "lending-map__overlay__table-row" +
                        (index > 0
                          ? " lending-map__overlay__table-row--secondary"
                          : "")
                      }
                    >
                      <td className="title--meta title--light">
                        {index > 0 ? (
                          <span className="util__screen-reader-only">
                            {country.name}
                          </span>
                        ) : (
                          country.name
                        )}
                      </td>
                      <td className="title--meta title--light">
                        {agreement.approval_date}
                      </td>
                      <td className="title--meta title--light">
                        {agreement.amount === 0 ? (
                          "*"
                        ) : (
                          <>
                            {roundNumber(agreement.amount, "millions", "all")}
                            <span> Million</span>
                            {agreement.augmented && (
                              <span class="lending-map__overlay__table-row__augmented-dagger">
                                <sup>†</sup>
                              </span>
                            )}
                          </>
                        )}
                      </td>
                      <td className="title--meta title--light">
                        <a
                          href={agreement.link}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <IconExternalLink />
                        </a>
                      </td>
                    </tr>
                  )
                })
              )
            })}
        </tbody>
      </table>
    </div>
  )
}

// General subcomponent
function AgreementTypeKey({ type, color, uppercase, augmented }) {
  return (
    <h4
      className={
        "agreement-type-key" +
        (uppercase ? " agreement-type-key--uppercase" : "")
      }
      style={{ color: color }}
    >
      {augmented && "Aug. of "}
      {type}
    </h4>
  )
}

//
// Utilities
//

// Returns an object containing arrays of the agreement color names and color values that match the given dataset
function getColors(dataset) {
  // Initialize arrays
  let colorNames = [],
    colorValues = []

  dataset.agreements.forEach(datasetAgreement => {
    let agreement = findObject(
      lendingMapAgreements,
      "abbreviation",
      datasetAgreement.abbreviation
    )

    if (!colorNames.includes(agreement.color.name)) {
      colorNames.push(agreement.color.name) // Only push novel color names
    }

    if (!colorValues.includes(agreement.color.hex)) {
      colorValues.push(agreement.color.hex) // Only push novel color values
    }
  })

  return {
    colorNames,
    colorValues,
  }
}

// Returns the default fill, agreement fill, or agreement fill pattern string to be used as the fill for a country
function getFill(country) {
  // Get the color names and values that apply to the country
  var { colorNames, colorValues } = getColors(country)

  // If there is more than one fill color, pass a predictable URL string for the fill, then generate a <linearGradient> to inject into the svg <defs>
  if (colorNames.length > 1 && colorValues.length > 1) {
    let fillPatternString = `pattern-stripe-${colorNames.sort().join("-")}`

    return `url('#${fillPatternString}')`
  }

  // If there is only one fill color, pass its value
  return colorValues[0]
}

// Returns an array of only the countries with agreements (and only the agreements therein) that match the filter
// H/T https://stackoverflow.com/a/42167998
function filterDataByFilter(dataset, filter) {
  return dataset.reduce(function (acc, country) {
    const agreements = country.agreements.filter(
      agreement => agreement[filter] === true
    )
    return !agreements.length
      ? acc
      : acc.concat(
          Object.assign({}, country, {
            agreements,
          })
        )
  }, [])
}

// Modified version of the above function to be consolidated later
// Returns an array of only the countries with agreements (and only the agreements therein) that match the agreement type
function filterDataByAgreement(dataset, agreementAbbreviation) {
  return dataset.reduce(function (acc, country) {
    const agreements = country.agreements.filter(
      agreement => agreement.abbreviation === agreementAbbreviation
    )
    return !agreements.length
      ? acc
      : acc.concat(
          Object.assign({}, country, {
            agreements,
          })
        )
  }, [])
}

// Returns the sum of all agreements of a given type across all countries of a dataset
function getAgreementTotal(dataset, agreementAbbreviation) {
  let agreementTotal = 0 // Initialize the total at zero to avoid NaN errors

  dataset.forEach(item => {
    item.agreements.forEach(agreement => {
      if (agreementAbbreviation === agreement.abbreviation) {
        agreementTotal += parseInt(agreement.amount)
      }
    })
  })

  return agreementTotal
}

// Returns the sum of all agreements across all countries of a dataset
function getAgreementsGrandTotal(dataset) {
  let agreementsGrandTotal = 0 // Initialize the total at zero to avoid NaN errors

  dataset.forEach(item => {
    item.agreements.forEach(agreement => {
      agreementsGrandTotal += parseInt(agreement.amount)
    })
  })

  return agreementsGrandTotal
}

export { LendingMap as default, getColors }
